import {
  APPLIES_TO,
  ALLOCATION_BASES,
  SHIPMENT_COST_KINDS,
  SHIPMENT_TRACKING_STATES,
  COUPON_KINDS,
  STOCK_FLAGS,
  STOCK_MOVEMENT_KINDS,
  STOCK_OWNERSHIPS,
  MESSAGE_CHANNELS,
  MESSAGE_KINDS,
  MESSAGE_STATUSES,
  MESSAGE_SUPPRESSION_REASONS,
  PROMOTION_EFFECT_KINDS,
  PROMOTION_VERSION_STATUSES,
  PURCHASE_ORDER_STATES,
  V1_MESSAGE_CHANNELS,
  RECALL_KINDS,
  RECALL_STATUSES,
  SALE_TYPES,
  STAFF_NOTIFICATION_KINDS,
  SUPPRESSION_REASONS,
  TENDERS,
  ATTRIBUTION_ROLES,
  CORRECTION_TYPES,
  SUPPLIER_CHANNELS,
  RECALL_WITHHELD_REASONS,
  REMAKE_CAUSES,
  ROLES,
} from '@lensmira/domain'
import type {
  FrameMaterial,
  FrameRimType,
  RxSource,
  CorrectionType,
  SupplierChannel,
  Role,
  AppliesTo,
  AllocationBasis,
  ShipmentCostKind,
  ShipmentTrackingState,
  FindingKind,
  MatchVerdict,
  CashSessionStatus,
  CouponKind,
  StockFlag,
  StockMovementKind,
  StockOwnership,
  JobStatus,
  MessageChannel,
  MessageKind,
  MessageStatus,
  MessageSuppressionReason,
  OrderStatus,
  PrescriptionStatus,
  PromotionEffectKind,
  PromotionVersionStatus,
  PurchaseOrderState,
  ReturnsPosition,
  RxType,
  RecallKind,
  SuppressionReason,
  V1MessageChannel,
  RecallStatus,
  SaleType,
  StaffNotificationKind,
  Tender,
  AttributionRole,
  CommissionRounding,
  CommissionScheme,
  LineRule,
  RecallWithheldReason,
  RemakeCause,
  RemakeStatus,
  RemakeTreatment,
  TierRule,
} from '@lensmira/domain'
import type {
  ImportTemplateId,
  LensDesign,
  LensMaterial,
  LensSurfacing,
  StockAdjustmentReason,
} from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'

/**
 * A shopkeeper's words for Appendix E's enum values, in one place. `LOC-001`, Appendix S.
 *
 * **Four screens held their own copy of these tables and three of them disagreed.** A job in
 * `chasing` read "Chasing lab" on the order list, "Chasing lab" on the customer record and
 * "Chasing the lab" on the job itself; `qc_failed` was "QC failed" on two screens and "Quality
 * check failed" on the third. One state with three names is a thing a shopkeeper has to learn, and
 * nobody told them.
 *
 * The short form wins, because two of the three screens already used it and a chip on a list has
 * less room than a heading. `JobDetail`'s longer wording is gone; no test asserted it.
 *
 * **Only the words are here.** The chip bucket and the tone stay on the screens that draw them:
 * those are `DESIGN-TOKENS.md` §4.2 colour decisions, not copy, and they do not get translated.
 *
 * Every function takes `t` rather than reaching for a hook, so this file holds no React and the
 * tables can be read by a test without rendering anything.
 */

export type Translate = (key: MessageKey) => string

/**
 * `RX-001`, §5.2: the kinds of prescription.
 *
 * `contact_lens` is in the registry and the spectacle capture screen filters it out, because a
 * contact lens prescription is a different record (§6.4). The label exists because the customer
 * record shows both.
 */
const RX_TYPE: Readonly<Record<RxType, MessageKey>> = {
  distance: 'status.rxType.distance',
  near: 'status.rxType.near',
  bifocal: 'status.rxType.bifocal',
  progressive: 'status.rxType.progressive',
  occupational: 'status.rxType.occupational',
  safety: 'status.rxType.safety',
  contact_lens: 'status.rxType.contactLens',
}

/**
 * Where a prescription came from. `RXW-005`, Appendix E's `rx_source`.
 *
 * `RXW-005` `MUST`: "A customer-supplied prescription from an outside prescriber is a supported and
 * clearly-flagged source. Warranty and remake policy may differ for outside Rx." The flag is only
 * a flag if somebody can read it, and the raw enum value on a document a customer takes to another
 * practice is not a word in any language.
 */
/**
 * How a frame holds its lenses, in words. `CAT-001`, `CAT-003`, Appendix E's `frame_rim_type`.
 *
 * The enum value is what the database stores and what `requiresMountingData` reasons about; this
 * is what a shopkeeper picks from a list. `semi_rimless` is not a phrase anybody says, and the
 * spelling mattering at all is why the registry exists: `tools/seed` wrote `half_rim` and the
 * domain asked about `semi_rimless`, so `CAT-003`'s lab guard never fired on a seeded frame.
 */
const FRAME_RIM_TYPE: Readonly<Record<FrameRimType, MessageKey>> = {
  full_rim: 'status.rimType.fullRim',
  semi_rimless: 'status.rimType.semiRimless',
  rimless: 'status.rimType.rimless',
}

export const frameRimTypeLabel = (t: Translate, rim: string): string => {
  const key = FRAME_RIM_TYPE[rim as FrameRimType]
  return key === undefined ? rim : t(key)
}

/** What a frame is made of. `CAT-001`'s five, Appendix E's `frame_material`. */
const FRAME_MATERIAL: Readonly<Record<FrameMaterial, MessageKey>> = {
  acetate: 'status.frameMaterial.acetate',
  metal: 'status.frameMaterial.metal',
  titanium: 'status.frameMaterial.titanium',
  tr90: 'status.frameMaterial.tr90',
  rimless_mount: 'status.frameMaterial.rimlessMount',
}

export const frameMaterialLabel = (t: Translate, material: string): string => {
  const key = FRAME_MATERIAL[material as FrameMaterial]
  return key === undefined ? material : t(key)
}

const RX_SOURCE: Readonly<Record<RxSource, MessageKey>> = {
  in_house: 'status.rxSource.inHouse',
  customer_supplied: 'status.rxSource.customerSupplied',
  transcribed: 'status.rxSource.transcribed',
  imported: 'status.rxSource.imported',
  ocr_assisted: 'status.rxSource.ocrAssisted',
}

/** A source this build does not know falls back to the raw value rather than to an empty cell. */
export const rxSourceLabel = (t: Translate, source: string): string => {
  const key = RX_SOURCE[source as RxSource]
  return key === undefined ? source : t(key)
}

/** `RX-002`: a prescription is immutable and versioned, so `superseded` is a state and not a delete. */
const RX_STATUS: Readonly<Record<PrescriptionStatus, MessageKey>> = {
  draft: 'status.rx.draft',
  active: 'status.rx.active',
  superseded: 'status.rx.superseded',
  expired: 'status.rx.expired',
  discarded: 'status.rx.discarded',
}

/** Appendix C.1, the order lifecycle. */
const ORDER_STATUS: Readonly<Record<OrderStatus, MessageKey>> = {
  draft: 'status.order.draft',
  quoted: 'status.order.quoted',
  confirmed: 'status.order.confirmed',
  partially_paid: 'status.order.partiallyPaid',
  paid: 'status.order.paid',
  fulfilled: 'status.order.fulfilled',
  closed: 'status.order.closed',
  expired: 'status.order.expired',
  cancelled: 'status.order.cancelled',
  refunded: 'status.order.refunded',
  on_hold: 'status.order.onHold',
}

/** Appendix C.2, the job lifecycle. Fifteen states, and `JOB-005a` makes `ready` unreachable
 *  except from a passed quality check. */
const JOB_STATUS: Readonly<Record<JobStatus, MessageKey>> = {
  draft: 'status.job.draft',
  ordered: 'status.job.ordered',
  sent: 'status.job.sent',
  chasing: 'status.job.chasing',
  acknowledged: 'status.job.acknowledged',
  in_production: 'status.job.inProduction',
  shipped: 'status.job.shipped',
  received: 'status.job.received',
  qc: 'status.job.qc',
  qc_failed: 'status.job.qcFailed',
  remake_raised: 'status.job.remakeRaised',
  ready: 'status.job.ready',
  collected: 'status.job.collected',
  on_hold: 'status.job.onHold',
  cancelled: 'status.job.cancelled',
}

/** §6.19, the till session. `variance_pending` is `CASH-004`'s approval gate. */
const CASH_SESSION_STATUS: Readonly<Record<CashSessionStatus, MessageKey>> = {
  open: 'status.cashSession.open',
  counting: 'status.cashSession.counting',
  variance_pending: 'status.cashSession.variancePending',
  approved: 'status.cashSession.approved',
  closed: 'status.cashSession.closed',
}

/**
 * `SAL-012`: what a line can do at the returns desk. `ReturnLine` and `OrderDetail` each held this
 * table and agreed word for word; `OrderDetail`'s comment said it "mirrors" the other so the two
 * could not drift, which is the thing a shared table does without a comment.
 */
const RETURNS_POSITION: Readonly<Record<ReturnsPosition, MessageKey>> = {
  returnable: 'returns.position.returnable',
  made_to_specification: 'returns.position.madeToSpecification',
  policy_dependent: 'returns.position.policyDependent',
}

/**
 * `SAL-001`, Appendix E: the twelve sale types, as the order form words them.
 *
 * `newOrder.saleType.*` rather than `orderDetail.saleType.*`, and that split is deliberate rather
 * than a leftover: `OrderDetail` words one sale type differently ("Lenses only, own frame" against
 * "Lenses only (own frame)") and keeps its own table with the note saying so. This one belongs to
 * the screens that *ask* for a sale type, which is `NewOrder` and now `Dispensing`, and it is here
 * because the third copy is the one that starts the drift the rest of this file exists to undo.
 */
const SALE_TYPE: Readonly<Record<SaleType, MessageKey>> = {
  complete_spectacles: 'newOrder.saleType.completeSpectacles',
  lenses_only_own_frame: 'newOrder.saleType.lensesOnlyOwnFrame',
  frame_only: 'newOrder.saleType.frameOnly',
  single_lens_replacement: 'newOrder.saleType.singleLensReplacement',
  plano_sunglasses: 'newOrder.saleType.planoSunglasses',
  prescription_sunglasses: 'newOrder.saleType.prescriptionSunglasses',
  ready_readers: 'newOrder.saleType.readyReaders',
  contact_lenses: 'newOrder.saleType.contactLenses',
  cl_solutions: 'newOrder.saleType.clSolutions',
  repair_or_adjustment: 'newOrder.saleType.repairOrAdjustment',
  spare_parts: 'newOrder.saleType.spareParts',
  eye_test_only: 'newOrder.saleType.eyeTestOnly',
}

/**
 * Appendix E `tender`, the nine ways a shop can be paid. `POS-003`.
 *
 * `OrderDetail` held this as `TENDER_KEY` and `TakePayment` as `METHOD_LABEL`, identical word for
 * word, and the receipt made three. Unlike the sale types there was never a wording question here:
 * both copies already agreed, so sharing them changes nothing on screen.
 */
const TENDER: Readonly<Record<Tender, MessageKey>> = {
  cash: 'tender.cash',
  card: 'tender.card',
  bank_transfer: 'tender.bankTransfer',
  wallet: 'tender.wallet',
  store_credit: 'tender.storeCredit',
  gift_card: 'tender.giftCard',
  panel: 'tender.panel',
  voucher: 'tender.voucher',
  cod: 'tender.cod',
}

/**
 * Appendix E's `staff_notification_kind`, the eight things a shop is told about. §6.26 `MSG-005a`.
 *
 * The kind is a category and the server's `summary` is the sentence: the kind says "till variance
 * above threshold" and the summary says which till and by how much. Both are shown, because a list
 * of eight repeated categories is unreadable and a list of sentences with no category cannot be
 * scanned.
 */
const NOTIFICATION_KIND: Readonly<Record<StaffNotificationKind, MessageKey>> = {
  job_overdue: 'notificationKind.jobOverdue',
  stock_out: 'notificationKind.stockOut',
  till_variance_above_threshold: 'notificationKind.tillVariance',
  dispute_opened: 'notificationKind.disputeOpened',
  subscription_past_due: 'notificationKind.subscriptionPastDue',
  lab_query_unanswered: 'notificationKind.labQueryUnanswered',
  ai_quota_exhausted: 'notificationKind.aiQuotaExhausted',
  reservation_expired: 'notificationKind.reservationExpired',
}

/** `APT-004`'s four recall rules, in the order the requirement lists them. */
const RECALL_KIND: Readonly<Record<RecallKind, MessageKey>> = {
  sight_test: 'recallKind.sightTest',
  contact_lens: 'recallKind.contactLens',
  frame_refresh: 'recallKind.frameRefresh',
  warranty_expiry: 'recallKind.warrantyExpiry',
}

/**
 * The recall lifecycle, section 6.24.
 *
 * `withheld` is a state and not a skipped row (`APT-005`), and `expired` keeps an old entry out of
 * today's queue without deleting it, so both need a word a shopkeeper can read on a list.
 */
const RECALL_STATUS: Readonly<Record<RecallStatus, MessageKey>> = {
  due: 'recallStatus.due',
  withheld: 'recallStatus.withheld',
  sent: 'recallStatus.sent',
  responded: 'recallStatus.responded',
  converted: 'recallStatus.converted',
  expired: 'recallStatus.expired',
}

/**
 * Appendix C.5, the purchase order lifecycle.
 *
 * Ten states for what a shopkeeper experiences as four questions: have I sent it, has the supplier
 * said yes, has it arrived, is it settled. The words keep that reading, so `invoice_matched` is
 * "Invoice agrees" rather than a process name: the shop's interest in the state is whether there is
 * anything left to argue about.
 */
const PURCHASE_ORDER_STATUS: Readonly<Record<PurchaseOrderState, MessageKey>> = {
  draft: 'purchaseOrderStatus.draft',
  sent: 'purchaseOrderStatus.sent',
  acknowledged: 'purchaseOrderStatus.acknowledged',
  partially_received: 'purchaseOrderStatus.partiallyReceived',
  received: 'purchaseOrderStatus.received',
  invoice_matched: 'purchaseOrderStatus.invoiceMatched',
  closed: 'purchaseOrderStatus.closed',
  cancelled: 'purchaseOrderStatus.cancelled',
  disputed: 'purchaseOrderStatus.disputed',
  resolved: 'purchaseOrderStatus.resolved',
}

/**
 * `IMP-006`'s six tracking states, in a shopkeeper's words.
 *
 * `received` is "Arrived" rather than "Received", because the word a shop uses for a goods receipt
 * is already "book in a delivery" on the purchasing screen, and the same word for both would read
 * as though the shipment itself had been booked into stock. Nothing about a shipment arriving puts
 * anything on a shelf: a goods receipt against its purchase orders does that, separately.
 *
 * State nouns and not verbs, because the same word is both the state on the row and the
 * destination in "Move it to {state}". A second verb table is what `JobDetail` needed and this
 * does not: there is one button per edge and no transition carries a different name from its
 * destination.
 */
const SHIPMENT_TRACKING: Readonly<Record<ShipmentTrackingState, MessageKey>> = {
  ordered: 'shipmentTracking.ordered',
  shipped: 'shipmentTracking.shipped',
  in_transit: 'shipmentTracking.inTransit',
  at_customs: 'shipmentTracking.atCustoms',
  cleared: 'shipmentTracking.cleared',
  received: 'shipmentTracking.received',
}

/** `IMP-001`'s seven non-goods costs, in the words a Karachi clearing agent's paperwork uses. */
const SHIPMENT_COST: Readonly<Record<ShipmentCostKind, MessageKey>> = {
  freight: 'shipmentCost.freight',
  insurance: 'shipmentCost.insurance',
  customs_duty: 'shipmentCost.customsDuty',
  clearing_agent: 'shipmentCost.clearingAgent',
  port_charges: 'shipmentCost.portCharges',
  inland_transport: 'shipmentCost.inlandTransport',
  bank_charges: 'shipmentCost.bankCharges',
}

/**
 * `IMP-002`'s four bases. Lower case on purpose: every one of these is read inside a sentence
 * ("Spread by {basis}", "It stays spread by {basis}") as well as standing alone in a select, and a
 * capital in the middle of the first reads as a proper noun.
 */
const ALLOCATION_BASIS: Readonly<Record<AllocationBasis, MessageKey>> = {
  value: 'allocationBasis.value',
  weight: 'allocationBasis.weight',
  volume: 'allocationBasis.volume',
  unit_count: 'allocationBasis.unitCount',
}

/**
 * `MSG-002`'s ten kinds of customer message, in a shopkeeper's words.
 *
 * `storefront_order_update` is registered and nothing produces one, because `V1-SPEC.md` §2 puts
 * the public storefront out of v1. It still needs a word: the outbox shows what is in the table,
 * and a row with no name would be a message nobody can account for.
 */
const MESSAGE_KIND: Readonly<Record<MessageKind, MessageKey>> = {
  order_confirmation: 'messageKind.orderConfirmation',
  deposit_receipt: 'messageKind.depositReceipt',
  job_status_change: 'messageKind.jobStatusChange',
  delay_notification: 'messageKind.delayNotification',
  ready_for_collection: 'messageKind.readyForCollection',
  uncollected_reminder: 'messageKind.uncollectedReminder',
  recall: 'messageKind.recall',
  appointment_reminder: 'messageKind.appointmentReminder',
  warranty_expiry: 'messageKind.warrantyExpiry',
  storefront_order_update: 'messageKind.storefrontOrderUpdate',
}

/** `MSG-001`: the two channels v1 can send on. WhatsApp and SMS are v3. */
const MESSAGE_CHANNEL: Readonly<Record<V1MessageChannel, MessageKey>> = {
  email: 'messageChannel.email',
  in_app: 'messageChannel.inApp',
}

/**
 * A chase channel, which is **every** channel and not `MSG-001`'s two.
 *
 * `MESSAGE_CHANNEL` above covers `V1_MESSAGE_CHANNELS`, email and in-app, because those are the two
 * this product can actually send on in v1. A loan-frame chase is not a send: `REP-005`'s chase is a
 * record that somebody tried to reach a customer, `LoanFramesService.chase` only inserts a row, and
 * it validates the channel against the domain's `MESSAGE_CHANNELS`. So a shopkeeper who messaged on
 * WhatsApp, which is how Pakistan is reached, can record having done it.
 *
 * Without this table that value would have rendered as the raw string `whatsapp` under `ARC-020`,
 * in English, in a screen that offered it from its own picker.
 */
const CHASE_CHANNEL: Readonly<Record<MessageChannel, MessageKey>> = {
  whatsapp: 'messageChannel.whatsapp',
  sms: 'messageChannel.sms',
  email: 'messageChannel.email',
  push: 'messageChannel.push',
  in_app: 'messageChannel.inApp',
}

/**
 * The message lifecycle, and the four distinctions it exists to keep.
 *
 * `sent` is not `delivered`, which `MSG-005` says in terms. `bounced` is about the address and
 * `failed` is about the attempt, which is why one is retried and the other is not. `suppressed` is
 * consent saying no and `cancelled` is the reason for the message going away. A screen that blurred
 * any pair would make the outbox unreadable at exactly the moment somebody needs it.
 */
const MESSAGE_STATUS: Readonly<Record<MessageStatus, MessageKey>> = {
  queued: 'messageStatus.queued',
  sending: 'messageStatus.sending',
  sent: 'messageStatus.sent',
  delivered: 'messageStatus.delivered',
  bounced: 'messageStatus.bounced',
  failed: 'messageStatus.failed',
  suppressed: 'messageStatus.suppressed',
  cancelled: 'messageStatus.cancelled',
}

/**
 * The move, as the button that makes it. Same eight values, different grammar.
 *
 * A status is a state the row is in and a move is a thing a person does, and the words differ:
 * "Failed" on a chip against "It did not go" on a button. One table of nouns used for both would
 * give the counter a button labelled with a state.
 */
const MESSAGE_MOVE: Readonly<Record<MessageStatus, MessageKey>> = {
  queued: 'outbox.move.queued',
  sending: 'outbox.move.sending',
  sent: 'outbox.move.sent',
  delivered: 'outbox.move.delivered',
  bounced: 'outbox.move.bounced',
  failed: 'outbox.move.failed',
  suppressed: 'outbox.move.suppressed',
  cancelled: 'outbox.move.cancelled',
}

/**
 * Why a message was not sent. `MSG-004`, `MSG-003`, `CUS-006`.
 *
 * Four different facts about three different subjects, and the service is careful never to record
 * one as another: `rx_opt_in_withdrawn` is the shop's decision and `suppressed` is the customer's,
 * so writing the first as the second would put the shop's choice on the customer's record.
 */
const SUPPRESSION_REASON: Readonly<Record<MessageSuppressionReason, MessageKey>> = {
  suppressed: 'suppressionReason.suppressed',
  no_consent: 'suppressionReason.noConsent',
  rx_opt_in_withdrawn: 'suppressionReason.rxOptInWithdrawn',
  customer_erased: 'suppressionReason.customerErased',
}

/**
 * `PRM-001`'s six effects, as a shop would describe what the promotion does.
 *
 * The words carry the arithmetic rather than the field name: "takes a percentage off" against
 * "sets the price", because a shopkeeper choosing between `amount_off` and `fixed_price` is
 * choosing between two different sums.
 */
const PROMOTION_EFFECT: Readonly<Record<PromotionEffectKind, MessageKey>> = {
  percent_off: 'promotionEffect.percentOff',
  amount_off: 'promotionEffect.amountOff',
  fixed_price: 'promotionEffect.fixedPrice',
  free_item: 'promotionEffect.freeItem',
  free_treatment: 'promotionEffect.freeTreatment',
  upgrade: 'promotionEffect.upgrade',
}

/**
 * Appendix X.2's `applies_to`, which decides how many times an effect comes off.
 *
 * `category` is accepted for a percentage and refused for an amount, because an amount off a
 * category has no defined meaning in the spec: once across the category or off every line in it
 * differ by the number of lines on the sale. The word is here for the percentage case.
 */
const APPLIES_TO_LABEL: Readonly<Record<AppliesTo, MessageKey>> = {
  line: 'appliesTo.line',
  order: 'appliesTo.order',
  category: 'appliesTo.category',
}

/** A promotion version is drafted, published, or withdrawn and kept. Nothing is deleted. */
const PROMOTION_STATUS: Readonly<Record<PromotionVersionStatus, MessageKey>> = {
  draft: 'promotionStatus.draft',
  published: 'promotionStatus.published',
  withdrawn: 'promotionStatus.withdrawn',
}

/**
 * Appendix X.3 step 5's four reasons a rule did not apply, in a shopkeeper's words.
 *
 * §15.16 puts these on the till: "A shopkeeper asked why the discount did not come off needs the
 * answer on the screen." So each reads as that answer rather than as the engine's term, and
 * `condition_false` is "this sale does not qualify" rather than "condition false", which is a
 * sentence about the code.
 */
const SUPPRESSION: Readonly<Record<SuppressionReason, MessageKey>> = {
  condition_false: 'suppression.conditionFalse',
  suppressed_by_non_stackable: 'suppression.nonStackable',
  suppressed_by_margin_floor: 'suppression.marginFloor',
  disabled: 'suppression.disabled',
}

/**
 * `PRM-005`'s two coupon kinds, as what they do rather than as their names.
 *
 * "Once, by one customer" against "as many times as you allow": the difference decides whether the
 * two cap fields mean anything, and the server refuses a cap on the first.
 */
const COUPON_KIND: Readonly<Record<CouponKind, MessageKey>> = {
  single_use: 'couponKind.singleUse',
  multi_use: 'couponKind.multiUse',
}

/**
 * `INV-003`'s three kinds of ownership, as what they mean for the shop's money.
 *
 * Consignment is saleable stock the shop has not bought, and `on_loan` is somebody else's frame on
 * the premises. The words have to keep those apart, because one is in owned valuation and the other
 * two are not.
 */
const OWNERSHIP: Readonly<Record<StockOwnership, MessageKey>> = {
  owned: 'ownership.owned',
  consignment: 'ownership.consignment',
  on_loan: 'ownership.onLoan',
}

/** What a movement was. `INV-001`, `INV-004`, `INV-005`. */
const MOVEMENT_KIND: Readonly<Record<StockMovementKind, MessageKey>> = {
  receipt: 'movementKind.receipt',
  sale: 'movementKind.sale',
  transfer_out: 'movementKind.transferOut',
  transfer_in: 'movementKind.transferIn',
  adjustment: 'movementKind.adjustment',
  reversal: 'movementKind.reversal',
}

/**
 * §12 case 47: the shelf and the record disagree.
 *
 * Worded as the thing to do about it rather than as the engine's term, because a shopkeeper reading
 * "negative stock" learns nothing they can act on.
 */
const STOCK_FLAG: Readonly<Record<StockFlag, MessageKey>> = {
  negative_stock: 'stockFlag.negative',
}

/**
 * `ROLE-002`'s seven, in words. Appendix B's own labels are the permission rows; these are the
 * people.
 *
 * **No role had ever been shown to a user.** Nothing in the counter printed one until the setup
 * screen offered a chooser, so there was no wording for any of the seven and a raw `read_only`
 * would have reached a shopkeeper through the `ARC-020` fallback.
 */
const ROLE: Readonly<Record<Role, MessageKey>> = {
  owner: 'role.owner',
  manager: 'role.manager',
  dispenser: 'role.dispenser',
  cashier: 'role.cashier',
  optician: 'role.optician',
  accountant: 'role.accountant',
  read_only: 'role.readOnly',
}

export const roleLabel = (t: Translate, value: string): string =>
  (ROLES as readonly string[]).includes(value) ? t(ROLE[value as Role]) : value

export const rxTypeLabel = (t: Translate, type: RxType): string => t(RX_TYPE[type])
/**
 * Appendix C.3's nine remake states.
 *
 * `ARC-020`: the value arrives unnarrowed from the read layer, so this takes a `string` and falls
 * back to it, the way `recallStatusWord` and `purchaseOrderStatusWord` do. A state this build does
 * not know shows as itself rather than as a blank chip.
 */
export const remakeStatusWord = (t: Translate, value: string): string =>
  value in REMAKE_STATUS ? t(REMAKE_STATUS[value as RemakeStatus]) : value
export const returnsPositionLabel = (t: Translate, position: ReturnsPosition): string =>
  t(RETURNS_POSITION[position])
export const rxStatusLabel = (t: Translate, status: PrescriptionStatus): string =>
  t(RX_STATUS[status])
export const orderStatusLabel = (t: Translate, status: OrderStatus): string =>
  t(ORDER_STATUS[status])
export const jobStatusLabel = (t: Translate, status: JobStatus): string => t(JOB_STATUS[status])
/**
 * The same word from an unnarrowed value, for the read layer. `ARC-020`, and the same shape
 * `recallStatusWord` and `purchaseOrderStatusWord` already take: a state this build does not know
 * shows as itself rather than as a blank chip.
 */
export const jobStatusWord = (t: Translate, value: string): string =>
  value in JOB_STATUS ? t(JOB_STATUS[value as JobStatus]) : value
export const orderStatusWord = (t: Translate, value: string): string =>
  value in ORDER_STATUS ? t(ORDER_STATUS[value as OrderStatus]) : value
export const cashSessionStatusLabel = (t: Translate, status: CashSessionStatus): string =>
  t(CASH_SESSION_STATUS[status])
export const saleTypeLabel = (t: Translate, type: SaleType): string => t(SALE_TYPE[type])

/**
 * The same word, for a value that arrived as a `string` and may not be one of the twelve.
 *
 * **Two screens read a sale type off the wire rather than off a registry**, because `ARC-020` keeps
 * a status or a sale type unnarrowed through the read layer: during a migration fan-out a newer
 * tenant schema sends a value this build has no word for, and refusing the read would turn a label
 * problem into an unusable screen. The hole that leaves is here: `SALE_TYPE[value]` misses,
 * `translate` returns its key, and the key is `undefined`, so the cell renders as nothing at all
 * and nothing anywhere says why. The raw value is ugly and true, which beats blank and silent.
 */
/**
 * A tender as it arrives off the wire, where it is a `string`.
 *
 * Same reasoning as {@link saleTypeWord}: `ARC-020` keeps the value unnarrowed through the read
 * layer, so a method this build has no word for shows as itself rather than as nothing. On a
 * receipt that matters more than on a screen, because the slip is what a customer keeps and a
 * blank beside an amount is a payment with no name.
 */
export const tenderWord = (t: Translate, value: string): string =>
  (TENDERS as readonly string[]).includes(value) ? t(TENDER[value as Tender]) : value

export const tenderLabel = (t: Translate, tender: Tender): string => t(TENDER[tender])

/**
 * A notification kind off the wire, where it is a `string`. Same `ARC-020` reasoning as
 * {@link saleTypeWord}: a kind a newer schema sends shows as itself rather than as nothing, because
 * a notification with no category beside its sentence is still actionable and a blank is not.
 */
export const notificationKindLabel = (t: Translate, value: string): string =>
  (STAFF_NOTIFICATION_KINDS as readonly string[]).includes(value)
    ? t(NOTIFICATION_KIND[value as StaffNotificationKind])
    : value

export const recallKindLabel = (t: Translate, value: string): string =>
  (RECALL_KINDS as readonly string[]).includes(value) ? t(RECALL_KIND[value as RecallKind]) : value

export const recallStatusWord = (t: Translate, value: string): string =>
  (RECALL_STATUSES as readonly string[]).includes(value)
    ? t(RECALL_STATUS[value as RecallStatus])
    : value

/**
 * A purchase order state off the wire, where it is a `string`. Same `ARC-020` reasoning as
 * {@link saleTypeWord}.
 */
export const purchaseOrderStatusWord = (t: Translate, value: string): string =>
  (PURCHASE_ORDER_STATES as readonly string[]).includes(value)
    ? t(PURCHASE_ORDER_STATUS[value as PurchaseOrderState])
    : value

/** A tracking state off the wire, where it is a `string`. `ARC-020`, as {@link saleTypeWord}. */
export const shipmentTrackingWord = (t: Translate, value: string): string =>
  (SHIPMENT_TRACKING_STATES as readonly string[]).includes(value)
    ? t(SHIPMENT_TRACKING[value as ShipmentTrackingState])
    : value

export const shipmentCostWord = (t: Translate, value: string): string =>
  (SHIPMENT_COST_KINDS as readonly string[]).includes(value)
    ? t(SHIPMENT_COST[value as ShipmentCostKind])
    : value

export const allocationBasisWord = (t: Translate, value: string): string =>
  (ALLOCATION_BASES as readonly string[]).includes(value)
    ? t(ALLOCATION_BASIS[value as AllocationBasis])
    : value

/** A message kind off the wire. Same `ARC-020` reasoning as {@link saleTypeWord}. */
export const messageKindLabel = (t: Translate, value: string): string =>
  (MESSAGE_KINDS as readonly string[]).includes(value)
    ? t(MESSAGE_KIND[value as MessageKind])
    : value

export const messageChannelWord = (t: Translate, value: string): string =>
  (V1_MESSAGE_CHANNELS as readonly string[]).includes(value)
    ? t(MESSAGE_CHANNEL[value as V1MessageChannel])
    : value

/**
 * A chase channel off the wire. Same `ARC-020` reasoning as {@link saleTypeWord}: a value this
 * build has no word for renders as itself rather than as a blank.
 */
export const chaseChannelWord = (t: Translate, value: string): string =>
  (MESSAGE_CHANNELS as readonly string[]).includes(value)
    ? t(CHASE_CHANNEL[value as MessageChannel])
    : value

export const messageStatusWord = (t: Translate, value: string): string =>
  (MESSAGE_STATUSES as readonly string[]).includes(value)
    ? t(MESSAGE_STATUS[value as MessageStatus])
    : value

/**
 * The button for a move. An unknown destination shows as itself, like every other wire value, but
 * this one is the screen's own list rather than the server's, so it should not happen.
 */
export const messageMoveLabel = (t: Translate, value: string): string =>
  (MESSAGE_STATUSES as readonly string[]).includes(value)
    ? t(MESSAGE_MOVE[value as MessageStatus])
    : value

/**
 * `RECALL_WITHHELD_REASONS`. Not the same set as `MESSAGE_SUPPRESSION_REASONS`, which is why it
 * gets its own table: a recall withheld because nobody consented to a channel is a different fact
 * from a message suppressed after a bounce, and the two sets happen to share one value.
 */
const RECALL_WITHHELD: Readonly<Record<RecallWithheldReason, MessageKey>> = {
  suppressed: 'recallWithheld.suppressed',
  no_consented_channel: 'recallWithheld.noConsentedChannel',
}

/**
 * Appendix E's `correction_type`. §15.25.
 *
 * Worded as what is wrong rather than as the enum's own name: a shopkeeper picking from this list
 * is answering "what is wrong with this record", so "The customer" reads where "Wrong customer"
 * would be the software describing its own column. `ARC-020`: a value this build has never heard
 * of falls back to itself rather than throwing, and the panel only ever offers what the server
 * said was offered.
 */
const CORRECTION_TYPE: Readonly<Record<CorrectionType, MessageKey>> = {
  wrong_customer: 'correctionType.wrongCustomer',
  wrong_staff: 'correctionType.wrongStaff',
  wrong_branch: 'correctionType.wrongBranch',
  wrong_job: 'correctionType.wrongJob',
  duplicate_entry: 'correctionType.duplicateEntry',
}

export const correctionTypeLabel = (t: Translate, value: string): string =>
  (CORRECTION_TYPES as readonly string[]).includes(value)
    ? t(CORRECTION_TYPE[value as CorrectionType])
    : value

/**
 * Appendix E's `supplier_channel`. §6.19 `SUP-003`.
 *
 * A table rather than a key built from the value in a template literal, which is how the screen
 * first wrote it: a key assembled at runtime is invisible to `i18n-static.test.ts`, which then
 * reports all four as English the product never uses. Keys are string literals here for that
 * reason, and camelCase because that guard's own key pattern has no underscore in it, so a
 * snake_case key is one it can never see used.
 *
 * `whatsapp_image` keeps a word even though `V1-SPEC.md` §2 puts WhatsApp out of v1 and the screen
 * does not offer it: a row imported or seeded can hold it, and `ARC-020` says a value this build
 * has never heard of still has to render.
 */
const SUPPLIER_CHANNEL: Readonly<Record<SupplierChannel, MessageKey>> = {
  api: 'supplierChannel.api',
  portal: 'supplierChannel.portal',
  email_structured: 'supplierChannel.emailStructured',
  whatsapp_image: 'supplierChannel.whatsappImage',
}

export const supplierChannelLabel = (t: Translate, value: string): string =>
  (SUPPLIER_CHANNELS as readonly string[]).includes(value)
    ? t(SUPPLIER_CHANNEL[value as SupplierChannel])
    : value

export const recallWithheldWord = (t: Translate, value: string): string =>
  (RECALL_WITHHELD_REASONS as readonly string[]).includes(value)
    ? t(RECALL_WITHHELD[value as RecallWithheldReason])
    : value

export const suppressionReasonWord = (t: Translate, value: string): string =>
  (MESSAGE_SUPPRESSION_REASONS as readonly string[]).includes(value)
    ? t(SUPPRESSION_REASON[value as MessageSuppressionReason])
    : value

export const promotionEffectLabel = (t: Translate, value: string): string =>
  (PROMOTION_EFFECT_KINDS as readonly string[]).includes(value)
    ? t(PROMOTION_EFFECT[value as PromotionEffectKind])
    : value

export const appliesToLabel = (t: Translate, value: string): string =>
  (APPLIES_TO as readonly string[]).includes(value)
    ? t(APPLIES_TO_LABEL[value as AppliesTo])
    : value

export const promotionStatusWord = (t: Translate, value: string): string =>
  (PROMOTION_VERSION_STATUSES as readonly string[]).includes(value)
    ? t(PROMOTION_STATUS[value as PromotionVersionStatus])
    : value

export const suppressionWord = (t: Translate, value: string): string =>
  (SUPPRESSION_REASONS as readonly string[]).includes(value)
    ? t(SUPPRESSION[value as SuppressionReason])
    : value

export const couponKindLabel = (t: Translate, value: string): string =>
  (COUPON_KINDS as readonly string[]).includes(value) ? t(COUPON_KIND[value as CouponKind]) : value

export const ownershipLabel = (t: Translate, value: string): string =>
  (STOCK_OWNERSHIPS as readonly string[]).includes(value)
    ? t(OWNERSHIP[value as StockOwnership])
    : value

export const movementKindLabel = (t: Translate, value: string): string =>
  (STOCK_MOVEMENT_KINDS as readonly string[]).includes(value)
    ? t(MOVEMENT_KIND[value as StockMovementKind])
    : value

export const stockFlagLabel = (t: Translate, value: string): string =>
  (STOCK_FLAGS as readonly string[]).includes(value) ? t(STOCK_FLAG[value as StockFlag]) : value

export const saleTypeWord = (t: Translate, value: string): string =>
  (SALE_TYPES as readonly string[]).includes(value) ? saleTypeLabel(t, value as SaleType) : value

/**
 * §6.24's vocabulary. `COM-001` to `COM-004` and `COM-007`.
 *
 * **Typed against the domain's own unions, not against a tuple, because §6.24 has no registry.**
 * Appendix E registers no commission enums and Appendix C has no commission state machine, so
 * `COMMISSION_VERSION_STATUSES`, `COMMISSION_ROUNDINGS` and `COMMISSION_EARNS_ON` live in
 * `packages/db/src/tenant/commission.ts`, which `apps/counter` does not depend on and must not. The
 * bases, scopes, measures and deduction kinds exist in `packages/domain` only as members of a
 * discriminated union, with no runtime tuple to walk.
 *
 * So each table below is a `Record` keyed by the domain type where there is one: a basis or a
 * rounding mode added to `packages/domain/src/commission.ts` is a type error here rather than a
 * value that renders as a slug. The scheme version statuses are the one table with nothing to key
 * on, and the three strings are written out with that stated.
 *
 * **Borrowing `PROMOTION_VERSION_STATUSES` would have typechecked**, because it is the same three
 * words, and it would have tied a pay rule's states to a promotion's for good. The real fix is to
 * move the three `db` tuples into `domain`, which this slice records rather than does.
 */
const COMMISSION_STATUS: Readonly<Record<string, MessageKey>> = {
  draft: 'commissionStatus.draft',
  published: 'commissionStatus.published',
  withdrawn: 'commissionStatus.withdrawn',
}

const COMMISSION_ROUNDING: Readonly<Record<CommissionRounding, MessageKey>> = {
  half_up: 'commissionRounding.halfUp',
  down: 'commissionRounding.down',
}

const COMMISSION_EARNS_ON: Readonly<Record<CommissionScheme['earnsOn'], MessageKey>> = {
  sale: 'commissionEarnsOn.sale',
  full_payment: 'commissionEarnsOn.fullPayment',
}

/** `COM-002`'s four bases. `flat` and `lens_tier` are kept apart: they pay on different inputs. */
const COMMISSION_BASIS: Readonly<Record<LineRule['basis'], MessageKey>> = {
  flat: 'commissionBasis.flat',
  retail: 'commissionBasis.retail',
  margin: 'commissionBasis.margin',
  lens_tier: 'commissionBasis.lensTier',
}

/**
 * Whether a line discount reduces the pay as well. The spec says only "percentage of retail".
 *
 * Keyed off the retail member of the union rather than off `'list' | 'net'` written again here, so
 * a third treatment added to `LineRule` is a type error rather than a value with no word.
 */
type RetailBase = Extract<LineRule, { basis: 'retail' }>['base']

const COMMISSION_BASE: Readonly<Record<RetailBase, MessageKey>> = {
  list: 'commissionBase.list',
  net: 'commissionBase.net',
}

const COMMISSION_TIER_SCOPE: Readonly<Record<TierRule['scope'], MessageKey>> = {
  person: 'commissionTierScope.person',
  branch: 'commissionTierScope.branch',
}

const COMMISSION_TIER_MEASURE: Readonly<Record<TierRule['measure'], MessageKey>> = {
  retail: 'commissionTierMeasure.retail',
  margin: 'commissionTierMeasure.margin',
}

const COMMISSION_DEDUCTION: Readonly<Record<RemakeTreatment['deduction']['kind'], MessageKey>> = {
  none: 'commissionDeduction.none',
  fixed: 'commissionDeduction.fixed',
  line_commission: 'commissionDeduction.lineCommission',
}

/** `COM-003`'s four roles, in the spec's own resolution order. */
const ATTRIBUTION_ROLE: Readonly<Record<AttributionRole, MessageKey>> = {
  dispense_creator: 'attributionRole.dispenseCreator',
  payment_taker: 'attributionRole.paymentTaker',
  fitter: 'attributionRole.fitter',
  handover: 'attributionRole.handover',
}

/**
 * Appendix E's ten remake causes, and the first screen in this app to need words for them.
 *
 * `COM-007` only names "wrong Rx entered, wrong measurement"; the other eight are in the registry
 * and a shop chooses from all ten, so all ten have a word. Nothing softens the two that are the
 * shop's own mistake: a statement that reduces somebody's pay has to say why in the words the
 * person would use about it.
 */
/** Appendix C.3. The queue is the first screen that had to name one. */
const REMAKE_STATUS: Readonly<Record<RemakeStatus, MessageKey>> = {
  raised: 'status.remake.raised',
  cause_assigned: 'status.remake.causeAssigned',
  costed: 'status.remake.costed',
  credit_expected: 'status.remake.creditExpected',
  credit_received: 'status.remake.creditReceived',
  new_job_created: 'status.remake.newJobCreated',
  in_progress: 'status.remake.inProgress',
  resolved: 'status.remake.resolved',
  rejected: 'status.remake.rejected',
}

const REMAKE_CAUSE: Readonly<Record<RemakeCause, MessageKey>> = {
  lab_error: 'remakeCause.labError',
  lab_breakage: 'remakeCause.labBreakage',
  shop_rx_error: 'remakeCause.shopRxError',
  shop_measurement_error: 'remakeCause.shopMeasurementError',
  prescriber_change: 'remakeCause.prescriberChange',
  non_adapt: 'remakeCause.nonAdapt',
  frame_warranty: 'remakeCause.frameWarranty',
  coating_defect: 'remakeCause.coatingDefect',
  customer_damage: 'remakeCause.customerDamage',
  insurer_change: 'remakeCause.insurerChange',
}

/**
 * Whether a table has a word for a value **as its own key**.
 *
 * `value in TABLE` would have done, and it answers true for `'constructor'`, `'toString'` and
 * every other name on `Object.prototype`. The value comes off the wire unnarrowed (`ARC-020`), so
 * a basis of `"constructor"` would have taken the `in` branch, read a function out of the
 * prototype, handed it to `t()` as a key, and had React render a function where a pay rule's basis
 * belongs. The eight tables below have no domain tuple to walk, which is why they cannot use the
 * `includes` guard the rest of this file uses, and this is the equivalent that is still exact.
 */
const hasWord = (table: object, value: string): boolean =>
  Object.prototype.hasOwnProperty.call(table, value)

export const commissionStatusWord = (t: Translate, value: string): string =>
  hasWord(COMMISSION_STATUS, value) ? t(COMMISSION_STATUS[value] as MessageKey) : value

export const commissionRoundingLabel = (t: Translate, value: string): string =>
  hasWord(COMMISSION_ROUNDING, value) ? t(COMMISSION_ROUNDING[value as CommissionRounding]) : value

export const commissionEarnsOnLabel = (t: Translate, value: string): string =>
  hasWord(COMMISSION_EARNS_ON, value)
    ? t(COMMISSION_EARNS_ON[value as CommissionScheme['earnsOn']])
    : value

export const commissionBasisLabel = (t: Translate, value: string): string =>
  hasWord(COMMISSION_BASIS, value) ? t(COMMISSION_BASIS[value as LineRule['basis']]) : value

export const commissionBaseLabel = (t: Translate, value: string): string =>
  hasWord(COMMISSION_BASE, value) ? t(COMMISSION_BASE[value as RetailBase]) : value

export const commissionTierScopeLabel = (t: Translate, value: string): string =>
  hasWord(COMMISSION_TIER_SCOPE, value)
    ? t(COMMISSION_TIER_SCOPE[value as TierRule['scope']])
    : value

export const commissionTierMeasureLabel = (t: Translate, value: string): string =>
  hasWord(COMMISSION_TIER_MEASURE, value)
    ? t(COMMISSION_TIER_MEASURE[value as TierRule['measure']])
    : value

export const commissionDeductionLabel = (t: Translate, value: string): string =>
  hasWord(COMMISSION_DEDUCTION, value)
    ? t(COMMISSION_DEDUCTION[value as RemakeTreatment['deduction']['kind']])
    : value

export const attributionRoleLabel = (t: Translate, value: string): string =>
  (ATTRIBUTION_ROLES as readonly string[]).includes(value)
    ? t(ATTRIBUTION_ROLE[value as AttributionRole])
    : value

export const remakeCauseLabel = (t: Translate, value: string): string =>
  (REMAKE_CAUSES as readonly string[]).includes(value)
    ? t(REMAKE_CAUSE[value as RemakeCause])
    : value

/**
 * Appendix W.5's verdicts. `MatchVerdict` in `packages/domain/src/purchasing.ts`.
 *
 * `pending` is the one worth wording carefully: it is not a failure, it is an order that is still
 * part-delivered, and `matchThreeWay` returns it for every invoice until the goods are all in.
 */
const MATCH_VERDICT: Readonly<Record<MatchVerdict, MessageKey>> = {
  matched: 'matchVerdict.matched',
  pending: 'matchVerdict.pending',
  query: 'matchVerdict.query',
}

/**
 * `INV-006`'s six disagreements. `FindingKind` in `packages/domain/src/purchasing.ts`, whose own
 * comment says what each one means and which two of the three statements it compares.
 */
const MATCH_FINDING: Readonly<Record<FindingKind, MessageKey>> = {
  short_shipment: 'matchFinding.shortShipment',
  over_shipment: 'matchFinding.overShipment',
  invoice_quantity_over: 'matchFinding.invoiceQuantityOver',
  invoice_quantity_under: 'matchFinding.invoiceQuantityUnder',
  price_variance: 'matchFinding.priceVariance',
  unknown_invoice_line: 'matchFinding.unknownInvoiceLine',
}

/**
 * `supplier_invoice.match_status`, whose three values are `INVOICE_MATCH_STATUSES` in
 * `packages/db/src/tenant/purchasing.ts` and are computed from the verdict in
 * `purchasing.service.ts`.
 *
 * Keyed on a union written here because there is nowhere else to read it from: the values are a
 * database check constraint and a service-level union, and Appendix E's registry in
 * `packages/domain` does not carry them the way it carries `PURCHASE_ORDER_STATES`. That is the
 * gap, not this table; moving the list into the domain is the fix and it is recorded in the
 * handoff.
 */
type InvoiceMatchStatus = 'unmatched' | 'matched' | 'queried'

const INVOICE_MATCH_STATUS: Readonly<Record<InvoiceMatchStatus, MessageKey>> = {
  unmatched: 'invoiceMatchStatus.unmatched',
  matched: 'invoiceMatchStatus.matched',
  queried: 'invoiceMatchStatus.queried',
}

/**
 * The three readers below test membership with `Object.hasOwn` rather than against a domain array,
 * because none of these three enums has one. `Object.hasOwn` and not `in`, so an inherited name
 * like `toString` off the wire does not resolve to a table entry and render as somebody's word.
 */
export const matchVerdictWord = (t: Translate, value: string): string =>
  Object.hasOwn(MATCH_VERDICT, value) ? t(MATCH_VERDICT[value as MatchVerdict]) : value

export const matchFindingWord = (t: Translate, value: string): string =>
  Object.hasOwn(MATCH_FINDING, value) ? t(MATCH_FINDING[value as FindingKind]) : value

export const invoiceMatchStatusWord = (t: Translate, value: string): string =>
  Object.hasOwn(INVOICE_MATCH_STATUS, value)
    ? t(INVOICE_MATCH_STATUS[value as InvoiceMatchStatus])
    : value

/**
 * Every table, so a test can walk them and prove no enum value is missing a word.
 *
 * `Record<Enum, MessageKey>` already makes a missing value a type error, which is most of the
 * guarantee. What it cannot catch is a key that is not in the catalogue, and the type of
 * `MessageKey` is what catches that. This is here so a test can also prove the tables are not
 * silently empty, which is the shape of failure a refactor produces.
 */
/**
 * Why each import template cannot be committed, or `null` for the one that can.
 *
 * `migration-support.ts` holds `NOT_COMMITTABLE_BECAUSE` with a sentence per template naming the
 * column that has nowhere to land. Those sentences are the server's and are written for a
 * developer; these are the same reasons for a shopkeeper, and they live in a table rather than at
 * the call site because a computed key is invisible to the i18n guard.
 *
 * Exhaustive over `ImportTemplateId` on purpose: a ninth template is then a type error here rather
 * than a screen that silently offers a commit the server refuses.
 */
const IMPORT_NOT_COMMITTABLE: Readonly<Record<ImportTemplateId, MessageKey | null>> = {
  customers: null,
  prescriptions: 'dataFiles.notCommittable.prescriptions',
  frames: 'dataFiles.notCommittable.frames',
  lenses: 'dataFiles.notCommittable.lenses',
  lens_prices: 'dataFiles.notCommittable.lensPrices',
  treatments: 'dataFiles.notCommittable.treatments',
  suppliers: 'dataFiles.notCommittable.suppliers',
  open_jobs: 'dataFiles.notCommittable.openJobs',
}

/** The sentence, or `null` when this template can be brought in. */
export const importNotCommittableWord = (t: Translate, id: ImportTemplateId): string | null => {
  const key = IMPORT_NOT_COMMITTABLE[id]
  return key === null ? null : t(key)
}

/**
 * Appendix E's `stock_adjustment_reason`, and which of them each direction allows.
 *
 * `applyMovement` narrows by sign: a positive delta is `count_variance` and nothing else, because
 * pieces appearing can only be a count that was wrong, while pieces leaving can be any of the
 * losses. `revaluation` and `return_to_vendor` are in the enum and neither branch takes them, so
 * they have a word here and appear in no list: that is what makes their absence a decision.
 */
const STOCK_REASON: Readonly<Record<StockAdjustmentReason, MessageKey>> = {
  count_variance: 'stockReason.countVariance',
  damage: 'stockReason.damage',
  shrinkage: 'stockReason.shrinkage',
  expiry: 'stockReason.expiry',
  write_off: 'stockReason.writeOff',
  revaluation: 'stockReason.revaluation',
  return_to_vendor: 'stockReason.returnToVendor',
  sample: 'stockReason.sample',
  in_house_breakage: 'stockReason.inHouseBreakage',
}

/** `LOSS_REASONS` plus `count_variance`, which is what a negative delta allows. */
export const STOCK_REASONS_TAKING_AWAY: readonly StockAdjustmentReason[] = [
  'count_variance',
  'damage',
  'shrinkage',
  'expiry',
  'write_off',
  'in_house_breakage',
  'sample',
]

/** What a positive delta allows, which is one thing. */
export const STOCK_REASONS_ADDING: readonly StockAdjustmentReason[] = ['count_variance']

export const stockReasonLabel = (t: Translate, value: string): string =>
  Object.hasOwn(STOCK_REASON, value) ? t(STOCK_REASON[value as StockAdjustmentReason]) : value

export const STATUS_LABEL_TABLES: Readonly<Record<string, Readonly<Record<string, MessageKey>>>> = {
  commissionStatus: COMMISSION_STATUS,
  stockReason: STOCK_REASON,
  commissionRounding: COMMISSION_ROUNDING,
  commissionEarnsOn: COMMISSION_EARNS_ON,
  commissionBasis: COMMISSION_BASIS,
  commissionBase: COMMISSION_BASE,
  commissionTierScope: COMMISSION_TIER_SCOPE,
  commissionTierMeasure: COMMISSION_TIER_MEASURE,
  commissionDeduction: COMMISSION_DEDUCTION,
  attributionRole: ATTRIBUTION_ROLE,
  remakeCause: REMAKE_CAUSE,
  remakeStatus: REMAKE_STATUS,
  rxType: RX_TYPE,
  rxStatus: RX_STATUS,
  orderStatus: ORDER_STATUS,
  jobStatus: JOB_STATUS,
  cashSessionStatus: CASH_SESSION_STATUS,
  saleType: SALE_TYPE,
  tender: TENDER,
  notificationKind: NOTIFICATION_KIND,
  recallKind: RECALL_KIND,
  recallStatus: RECALL_STATUS,
  recallWithheld: RECALL_WITHHELD,
  purchaseOrderStatus: PURCHASE_ORDER_STATUS,
  shipmentTracking: SHIPMENT_TRACKING,
  shipmentCost: SHIPMENT_COST,
  allocationBasis: ALLOCATION_BASIS,
  messageKind: MESSAGE_KIND,
  messageChannel: MESSAGE_CHANNEL,
  chaseChannel: CHASE_CHANNEL,
  messageStatus: MESSAGE_STATUS,
  messageMove: MESSAGE_MOVE,
  suppressionReason: SUPPRESSION_REASON,
  promotionEffect: PROMOTION_EFFECT,
  appliesTo: APPLIES_TO_LABEL,
  promotionStatus: PROMOTION_STATUS,
  suppression: SUPPRESSION,
  couponKind: COUPON_KIND,
  ownership: OWNERSHIP,
  movementKind: MOVEMENT_KIND,
  stockFlag: STOCK_FLAG,
  matchVerdict: MATCH_VERDICT,
  matchFinding: MATCH_FINDING,
  invoiceMatchStatus: INVOICE_MATCH_STATUS,
  correctionType: CORRECTION_TYPE,
  supplierChannel: SUPPLIER_CHANNEL,
} as const

/**
 * The four kinds of stock location. §6.30 `BR-04`, `BR-10`; §6.10 `REF-010`.
 *
 * **A kind rather than three booleans**, because `is_central_warehouse` and `is_trial` are mutually
 * exclusive and both force `is_selling` off, so a screen offering three ticks offers combinations
 * the server can only refuse. Derived from the flags when reading and expanded back when writing,
 * so the database keeps the flags and the shopkeeper sees four choices.
 */
export const LOCATION_KINDS = ['selling', 'back_store', 'warehouse', 'trial'] as const
export type LocationKind = (typeof LOCATION_KINDS)[number]

const LOCATION_KIND: Readonly<Record<LocationKind, MessageKey>> = {
  selling: 'stockLocations.kind.selling',
  back_store: 'stockLocations.kind.backStore',
  warehouse: 'stockLocations.kind.warehouse',
  trial: 'stockLocations.kind.trial',
}

/**
 * What choosing a kind does, shown under the chooser.
 *
 * A non-selling location is invisible at the counter, which is too large a consequence for a
 * shopkeeper to discover by finding their stock unsellable.
 */
const LOCATION_EFFECT: Readonly<Record<LocationKind, MessageKey>> = {
  selling: 'stockLocations.effect.selling',
  back_store: 'stockLocations.effect.backStore',
  warehouse: 'stockLocations.effect.warehouse',
  trial: 'stockLocations.effect.trial',
}

export const locationKindLabel = (t: Translate, kind: string): string => {
  const key = LOCATION_KIND[kind as LocationKind]
  return key === undefined ? kind : t(key)
}

export const locationKindEffect = (t: Translate, kind: string): string => {
  const key = LOCATION_EFFECT[kind as LocationKind]
  return key === undefined ? '' : t(key)
}

/** The kind a location's three flags add up to. Trial first, because it is the narrowest. */
export const locationKindOf = (location: {
  readonly isSelling: boolean
  readonly isCentralWarehouse: boolean
  readonly isTrial: boolean
}): LocationKind =>
  location.isTrial
    ? 'trial'
    : location.isCentralWarehouse
      ? 'warehouse'
      : location.isSelling
        ? 'selling'
        : 'back_store'

/** Appendix E `lens_design`. `CAT-010`'s design axis. */
const LENS_DESIGN: Readonly<Record<LensDesign, MessageKey>> = {
  single_vision: 'status.lensDesign.singleVision',
  bifocal: 'status.lensDesign.bifocal',
  trifocal: 'status.lensDesign.trifocal',
  progressive: 'status.lensDesign.progressive',
  occupational: 'status.lensDesign.occupational',
  degressive: 'status.lensDesign.degressive',
}

export const lensDesignLabel = (t: Translate, design: string): string => {
  const key = LENS_DESIGN[design as LensDesign]
  return key === undefined ? design : t(key)
}

/** Appendix E `lens_material`. `CAT-010` keeps the index separate, because 1.60 and 1.67 share one. */
const LENS_MATERIAL: Readonly<Record<LensMaterial, MessageKey>> = {
  cr39: 'status.lensMaterial.cr39',
  trivex: 'status.lensMaterial.trivex',
  polycarbonate: 'status.lensMaterial.polycarbonate',
  high_index_plastic: 'status.lensMaterial.highIndexPlastic',
  glass: 'status.lensMaterial.glass',
}

export const lensMaterialLabel = (t: Translate, material: string): string => {
  const key = LENS_MATERIAL[material as LensMaterial]
  return key === undefined ? material : t(key)
}

/** `CAT-010`'s surfacing axis: "stock / conventional surfaced / freeform digital". */
const LENS_SURFACING: Readonly<Record<LensSurfacing, MessageKey>> = {
  stock: 'status.lensSurfacing.stock',
  conventional_surfaced: 'status.lensSurfacing.conventionalSurfaced',
  freeform_digital: 'status.lensSurfacing.freeformDigital',
}

export const lensSurfacingLabel = (t: Translate, surfacing: string): string => {
  const key = LENS_SURFACING[surfacing as LensSurfacing]
  return key === undefined ? surfacing : t(key)
}

/**
 * Whether a design takes a corridor, and whether it takes an add range. `CAT-010`, `CAT-011`.
 *
 * The server refuses a corridor on anything but a progressive or a degressive, and an add range on
 * anything that is not multifocal. These are what let a form hide the box rather than offer one
 * whose contents will be rejected.
 */
export const needsCorridor = (design: string): boolean =>
  design === 'progressive' || design === 'degressive'

export const needsAddRange = (design: string): boolean =>
  design === 'bifocal' ||
  design === 'trifocal' ||
  design === 'progressive' ||
  design === 'occupational' ||
  design === 'degressive'

/** Appendix E's eleven treatment kinds. `CAT-012`. */
const TREATMENT_KIND: Readonly<Record<string, MessageKey>> = {
  hard_coat: 'status.treatment.hardCoat',
  ar_coating: 'status.treatment.arCoating',
  mirror_coat: 'status.treatment.mirrorCoat',
  photochromic: 'status.treatment.photochromic',
  polarised: 'status.treatment.polarised',
  tint_solid: 'status.treatment.tintSolid',
  tint_gradient: 'status.treatment.tintGradient',
  uv: 'status.treatment.uv',
  blue_light_filter: 'status.treatment.blueLightFilter',
  hydrophobic: 'status.treatment.hydrophobic',
  anti_fog: 'status.treatment.antiFog',
  edge_polish: 'status.treatment.edgePolish',
}

export const treatmentKindLabel = (t: Translate, kind: string): string => {
  const key = TREATMENT_KIND[kind]
  return key === undefined ? kind : t(key)
}

/** `CAT-012` makes colour and density a tint's properties, and the server refuses them elsewhere. */
export const isTintKind = (kind: string): boolean =>
  kind === 'tint_solid' || kind === 'tint_gradient'
